import Spinner from "../Spinner";
import styles from "./styles.module.scss";
import { getCustomers, getProductsList, getRoleInfo } from "../../api/config";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SelectOption, UserFormProps } from "../../interfaces/fe.interfaces";
import { GetCustomersResponse, GetProductListResponse, RoleInfoResponse } from "../../interfaces/be.interfaces";
import { useFetch } from "../../hooks/useFetch";
import Select from "../Select";
import { Icons } from "../../../assets/images";
import Checkbox from "../Checkbox";

/**
 * Common form component for AddUser and EditUser
 * Takes in all the props from Formik component
 */
const Form = ({
  values,
  handleChange,
  touched,
  errors,
  handleSubmit,
  handleReset,
  setFieldValue,
  handleBlur,
  isSubmitting,
  enableReinitialize,
  isEditForm,
  invite = false,
}: UserFormProps) => {
  const [customerList, setCustomerList] = useState<SelectOption<string | null>[]>([]);
  const [isPasswordVisible, setIsPasswordVisible] = useState({
    password: false,
    confirmPassword: false,
  });

  // Get list of customer info options to display in dropdown selection
  const {
    loading: roleInfoLoading,
    error: customerError,
    data: roleInfoData,
  } = useFetch<RoleInfoResponse>({
    url: getRoleInfo,
    runOnMount: true,
  });

  /**
   * Fetch list of customers to show in dropdown
   * only active customers are displayed in dropdown
   */
  const { loading: customerInfoLoading, data: customerInfoList } = useFetch<GetCustomersResponse>({
    runOnMount: true,
    url: getCustomers,
    onSuccess: (response) => {
      // Prepare customer data in option format to show in dropdown and filter inactive customers
      const customerOptions: SelectOption<string>[] = response.data?.data?.customers
        ?.filter((item) => item.is_enable)
        .map((customer) => ({
          label: customer.customer_name,
          value: customer.vendor_id,
        }));

      setCustomerList(customerOptions);
    },
  });

  const onAddRole = useCallback(
    (value: string) => {
      const role = roleInfoData?.data.list.find((item) => item.role_id === value);
      if (role) {
        const updatedRoles = [...values.roles, role];
        setFieldValue("roles", updatedRoles);
        setFieldValue("role_ids", [...values.role_ids, value]);

        // If this added role is a WISA role, handle customer selection
        if (role.is_wisa_role) {
          const customer = customerInfoList?.data.customers.find((item) => item.is_wisa === true);

          if (customer) {
            setFieldValue("customer", customer);
            setFieldValue("vendor_id", customer.vendor_id);
          }
        }
      }
    },
    [roleInfoData, customerInfoList, values.roles, values.role_ids]
  );

  // Check if any of the selected roles is a WISA role
  const hasWisaRole = useMemo(() => {
    return values.roles.some(role => role.is_wisa_role);
  }, [values.roles]);

  // Handle customer change
  const onChangeCustomer = useCallback(
    (value: string) => {
      const customer = customerInfoList?.data.customers.find((item) => item.vendor_id === value);
      setFieldValue("customer", customer);
      setFieldValue("vendor_id", value);
      setFieldValue("managed_product_ids", []);
    },
    [customerInfoList]
  );

  const onChangeIsPasswordVisible = useCallback((value: boolean) => {
    // Reset password and confirm password field when uncheck the password visible field
    if (!value) {
      setFieldValue("password", null);
      setFieldValue("confirmPassword", null);
    }

    setFieldValue("isPasswordFieldVisible", value);
  }, []);

  const { data: productList } = useFetch<GetProductListResponse>({
    url: getProductsList,
    runOnMount: true,
  });

  const productsOfCustomer = useMemo(() => {
    if (!values.vendor_id) return [];

    const productsOfVendor =
      productList?.data.list.filter(v => values.manage_customer_ids.includes(v.vendor_id))?.map(v => v.customer_products)?.flat() ?? [];

    return productsOfVendor
      .map((value) => ({
        label: value.product_name,
        value: value.customer_product_id,
      }))
      .filter((v) => !values.managed_product_ids.includes(v.value));
  }, [productList, values.vendor_id, values.managed_product_ids, values.manage_customer_ids]);

  const selectedManageProductItem = useMemo(() => {
    if (values.managed_product_ids.length) {
      const productsOfVendor =
        productList?.data.list.filter((v) => values.manage_customer_ids.includes(v.vendor_id))
          ?.map(v => v.customer_products)?.flat() ?? [];

      return productsOfVendor
        .map((value) => ({
          label: value.product_name,
          value: value.customer_product_id,
        }))
        .filter((v) => values.managed_product_ids.includes(v.value));
    }

    return [];
  }, [productList, values.vendor_id, values.managed_product_ids, values.manage_customer_ids]);

  useEffect(() => {
    if (values.role_ids.length === 0 || !hasWisaRole) {
      setFieldValue("manage_customer_ids", []);
      setFieldValue("managed_product_ids", []);
    }
  }, [values.role_ids, hasWisaRole]);

  return (
    <form onSubmit={handleSubmit}>
      {customerError ? <div className="errorAlert">{customerError}</div> : null}
      <div className={styles.dialog}>
        {isSubmitting || roleInfoLoading || customerInfoLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="w-100 d-flex justify-content-around">
              <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center`}>
                <Select
                  selectProps={{
                    name: "role",
                    placeholder: "Select User Role",
                    onChange: (event) => onAddRole(event.target.value),
                  }}
                  optionLabel="role_name"
                  optionValue="role_id"
                  options={roleInfoData?.data.list?.filter(role => !values.role_ids.includes(role.role_id)) ?? []}
                />
                {errors?.role_ids && touched.role_ids ? <p className={styles.error}>{errors?.role_ids}</p> : null}
              </div>
              <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center `}>
                <Select
                  selectProps={{
                    value: values.vendor_id,
                    placeholder: "Select Customer",
                    onChange: (event) => onChangeCustomer(event.target.value),
                    disabled: hasWisaRole,
                  }}
                  options={customerList}
                />
                {touched.vendor_id && errors.vendor_id && <p className={styles.error}>{errors.vendor_id}</p>}
              </div>
            </div>

            {values.roles.length > 0 && (
              <div className={`${styles.tagsContainer}`}>
                {values.roles.map((role) => (
                  <div key={role.role_id} className={styles.tagsItem}>
                    {role.role_name}
                    <span
                      onClick={() => {
                        setFieldValue(
                          "roles",
                          values.roles.filter((r) => r.role_id !== role.role_id)
                        );
                        setFieldValue(
                          "role_ids",
                          values.role_ids.filter((id) => id !== role.role_id)
                        );
                      }}>
                      x
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="w-100 d-flex justify-content-around">
              <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center`}>
                <input
                  name="email"
                  type="text"
                  placeholder={"Enter User Email"}
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="off"
                />
                {errors?.email && touched.email ? <p className={styles.error}>{errors?.email}</p> : null}
              </div>
              <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center`}>
                <input
                  name="name"
                  type="text"
                  placeholder={"Enter User Name"}
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="off"
                />
                {errors?.name && touched.name ? <p className={styles.error}>{errors?.name}</p> : null}
              </div>
            </div>

            {values.isPasswordFieldVisible && (
              <div className="w-100 d-flex justify-content-around">
                <div className={`${styles.inputContainer} d-flex justify-content-between`}>
                  <div className={styles.passwordContainer}>
                    <input
                      name="password"
                      type={isPasswordVisible.password ? "text" : "password"}
                      placeholder={"Enter User Password"}
                      value={values?.password ?? ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      onClick={() => setIsPasswordVisible({ ...isPasswordVisible, password: !isPasswordVisible.password })}>
                      {!isPasswordVisible.password ? <Icons.EyeOn /> : <Icons.EyeOff />}
                    </button>
                  </div>
                  {errors?.password && touched.password ? <p className={styles.error}>{errors?.password}</p> : null}
                </div>
                <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center`}>
                  <div className={styles.passwordContainer}>
                    <input
                      name="confirmPassword"
                      type={isPasswordVisible.confirmPassword ? "text" : "password"}
                      placeholder={"Enter Password Again"}
                      value={values.confirmPassword ?? ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      onClick={() => setIsPasswordVisible({ ...isPasswordVisible, confirmPassword: !isPasswordVisible.confirmPassword })}>
                      {!isPasswordVisible.confirmPassword ? <Icons.EyeOn /> : <Icons.EyeOff />}
                    </button>
                  </div>
                  {errors?.confirmPassword && touched.confirmPassword ? <p className={styles.error}>{errors?.confirmPassword}</p> : null}
                </div>
              </div>
            )}

            {invite === false ? (
              <>
                {hasWisaRole ? (
                  <>
                    <div className="w-100 d-flex justify-content-around">
                      <div
                        className={`${styles.inputContainer} ${styles.fullWidthSelect} d-flex justify-content-between align-item-center`}
                      >
                        <Select
                          selectProps={{
                            onChange: (event) => {
                              if (event.target.value) {
                                setFieldValue("manage_customer_ids", [...values.manage_customer_ids, event.target.value]);
                              }
                            },
                          }}
                          options={[{ label: "Select Customer To Manage", value: "" }, ...customerList.filter(
                            (customer) => !values.manage_customer_ids.includes(customer.value as string)
                          )]}
                        />
                        {errors?.manage_customer_ids && touched.manage_customer_ids ? (
                          <p className={styles.error}>{errors?.manage_customer_ids}</p>
                        ) : null}
                      </div>
                    </div>
                    {values.manage_customer_ids.length > 0 && (
                      <div className={`${styles.tagsContainer}`}>
                        {values.manage_customer_ids.map((customerId) => {
                          const customer = customerList.find((c) => c.value === customerId);
                          return (
                            <div key={customerId} className={styles.tagsItem}>
                              {customer?.label || customerId}
                              <span
                                onClick={() => {
                                  setFieldValue(
                                    "manage_customer_ids",
                                    values.manage_customer_ids.filter((id) => id !== customerId)
                                  );
                                  // Clear products if no customers are selected
                                  if (values.manage_customer_ids.length === 1) {
                                    setFieldValue("managed_product_ids", []);
                                  }
                                }}>
                                x
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : null}

                <div className="w-100 d-flex justify-content-around">
                  <div className={`${styles.inputContainer} ${styles.fullWidthSelect} d-flex justify-content-between align-item-center`}>
                    <Select
                      selectProps={{
                        onChange: (e) => setFieldValue("managed_product_ids", [...values.managed_product_ids, +e.target.value]),
                        disabled: !values.vendor_id || values.manage_customer_ids.length === 0,
                      }}
                      options={[{ label: "Select Products To Manage", value: "" }, ...productsOfCustomer]}
                    />
                    {errors?.managed_product_ids && touched.managed_product_ids ? (
                      <p className={styles.error}>{errors?.managed_product_ids}</p>
                    ) : null}
                  </div>
                </div>
                {selectedManageProductItem.length ? (
                  <div className={`${styles.tagsContainer}`}>
                    {selectedManageProductItem.map((value) => (
                      <div key={value.value} className={styles.tagsItem}>
                        {value.label}
                        <span
                          onClick={() => {
                            setFieldValue(
                              "managed_product_ids",
                              values.managed_product_ids.filter((v) => v !== value.value)
                            );
                          }}>
                          x
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className={`${styles.passwordCheckBox} w-100 d-flex`}>
                  <Checkbox
                    label="Is user customer point of contact?"
                    name="customer_point_of_contact"
                    checked={values?.customer_point_of_contact ?? false}
                    onChange={() => setFieldValue("customer_point_of_contact", !values?.customer_point_of_contact)}
                  />
                </div>
              </>
            ) : null}

            {isEditForm && (
              <div className={`${styles.passwordCheckBox} w-100 d-flex`}>
                <Checkbox
                  label="Change Password?"
                  name="isPasswordFieldVisible"
                  checked={values?.isPasswordFieldVisible ?? false}
                  onChange={() => onChangeIsPasswordVisible(!values.isPasswordFieldVisible)}
                />
              </div>
            )}

            <div className="w-100 d-flex justify-content-around">
              <button className={styles.dialogBtn} type="submit" disabled={isSubmitting}>
                {enableReinitialize ? "Update" : "Add"}
              </button>
              <button type="button" className={styles.dialogBtn} onClick={handleReset}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </form>
  );
};

export default Form;
